import * as React from 'react';
import classNames from 'classnames';
import Carousel, { ButtonGroupProps, ResponsiveType } from 'react-multi-carousel';

import './styles.scss';
import Icon from '../Icon';
import {useState, useCallback, useReducer, useRef} from 'react';

export interface IMultiCarouselProps {
    showDots?: boolean;
    showArrows?: boolean;
    showNumbers?: boolean;
    hideControls?: boolean;
    renderArrowsOutside?: boolean;
    type?: string;
    infinite?: boolean;
    autoPlay?: boolean;
    autoPlaySpeed?: number;
    transitionSpeed?: number;
    responsive?: ResponsiveType;
    ariaConfig?: IAriaConfig;
    onChange?: () => void;
}

export interface IAriaConfig {
    scrollNextButtonLabel?: string;
    scrollPrevButtonLabel?: string;
}

const CustomControls: React.FunctionComponent<ButtonGroupProps & IMultiCarouselProps &
    {
        autoPlaying: boolean;
        setAutoPlaying: (s: boolean) => void;
        infinite: boolean;
    }> = (props) => {
    return (
        <React.Fragment>
            {!!props.showArrows && (
                <React.Fragment>
                    <button onClick={() => {
                        if(props.next) { props.next(); }
                    }} className="control-arrow control-next" aria-label={props.ariaConfig ? props.ariaConfig.scrollNextButtonLabel : 'Next'}><Icon kind="arrow5-right" width={20} height={20}/></button>
                    <button onClick={() => {
                        if(props.previous) { props.previous(); }
                    }} className="control-arrow control-prev" aria-label={props.ariaConfig ? props.ariaConfig.scrollPrevButtonLabel : 'Prev'}><Icon kind="arrow5-left" width={20} height={20}/></button>
                </React.Fragment>
            )}
            {(!!props.autoPlay || !!props.showNumbers) && React.Children.toArray(props.children).length > 1 && !props.hideControls && (
                <React.Fragment>
                    {!!props.autoPlay && (
                        <button
                            className="control-pause tablet-hide desktop-hide"
                            aria-label={props.autoPlaying ? 'Pause' : 'Play'}
                            onClick={() => {
                                props.setAutoPlaying(!props.autoPlaying);
                            }}
                        >
                            <Icon kind={props.autoPlaying ? 'pause' : 'play'} height={20} width={20}/>
                        </button>
                    )}
                    <div className="react-multi-carousel-nav">
                        <div className="react-multi-carousel-nav__inner">
                            {!!props.autoPlay && (
                                <button
                                    className="control-pause mobile-hide"
                                    aria-label={props.autoPlaying ? 'Pause' : 'Play'}
                                    onClick={() => {
                                        props.setAutoPlaying(!props.autoPlaying);
                                    }}
                                >
                                    <Icon kind={props.autoPlaying ? 'pause' : 'play'} height={20} width={20}/>
                                </button>
                            )}
                            {React.Children.map(props.children, (child: any, i) => {
                                return (
                                    <button
                                        key={i}
                                        className={classNames('control-page', { current: props.carouselState?.currentSlide === i + (props.infinite ? 2 : 0), })}
                                        onClick={() => {
                                            if (props.goToSlide) {
                                                props.goToSlide(i + (props.infinite ? 2 : 0), false);
                                            }
                                        }}
                                    >
                                        {i + 1}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

const MultiCarousel: React.FunctionComponent<IMultiCarouselProps> = (props) => {
    const [isMounted, toggle] = useReducer((p) => !p, true);
    const [autoPlaying, setAutoPlaying] = useState(props.autoPlay || false);
    const infinite = typeof props.infinite !== 'undefined' ? props.infinite : true;

    const [inited, setInited] = useState<boolean>(false);

    let carouselInstance;

    const onChange = () => {
        if(typeof(carouselInstance)!=='undefined'){
            if(carouselInstance.containerRef!==null){
                if(carouselInstance.containerRef.current!==null){
                    const disableFocus = carouselInstance.containerRef.current.querySelectorAll('[aria-hidden="true"] A, [aria-hidden="true"] BUTTON, [aria-hidden="true"] INPUT, [aria-hidden="false"] INPUT');
                    const enableFocus = carouselInstance.containerRef.current.querySelectorAll('[aria-hidden="false"] A, [aria-hidden="false"] BUTTON');
                    enableFocus.forEach((item) => {
                        item.setAttribute('tabindex', 0);
                    });
                    disableFocus.forEach((item) => {
                        item.setAttribute('tabindex', -1);
                    });
                }
            }
        }
    };

    let responsive: ResponsiveType = {
        desktop: {
            // the naming can be any, depends on you.
            breakpoint: { max: 4000, min: 1280 },
            items: 4,
            slidesToSlide: 4,
        },
        narrowDesktop: {
            breakpoint: { max: 1279, min: 960 },
            items: 3,
            slidesToSlide: 3,
        },
        tablet: {
            breakpoint: { max: 959, min: 620 },
            items: 2,
            slidesToSlide: 2,
        },
        mobile: {
            breakpoint: { max: 619, min: 0 },
            items: 1,
        },
    };

    if (props.responsive) {
        responsive = props.responsive;
    }

    if (props.type === 'hero') {
        responsive = {
            desktop: {
                // the naming can be any, depends on you.
                breakpoint: { max: 4000, min: 1280 },
                items: 1,
            },
            narrowDesktop: {
                breakpoint: { max: 1279, min: 960 },
                items: 1,
            },
            tablet: {
                breakpoint: { max: 959, min: 620 },
                items: 1,
            },
            mobile: {
                breakpoint: { max: 619, min: 0 },
                items: 1,
            },
        };
    }
    if (props.type === 'onethird') {
        responsive = {
            desktop: {
                // the naming can be any, depends on you.
                breakpoint: { max: 4000, min: 1280 },
                items: 3,
                slidesToSlide: 3,
            },
            narrowDesktop: {
                breakpoint: { max: 1279, min: 960 },
                items: 3,
                slidesToSlide: 3,
            },
            tablet: {
                breakpoint: { max: 959, min: 850 },
                items: 3,
                slidesToSlide: 3,
            },
            mobile: {
                breakpoint: { max: 849, min: 0 },
                items: 1,
                slidesToSlide: 1,
            },
        };
    }
    if (props.type === 'productslider') {
        responsive = {
            desktop: {
                breakpoint: { max: 4000, min: 1280 },
                items: 5,
            },
            narrowDesktop: {
                breakpoint: { max: 1279, min: 960 },
                items: 4,
            },
            tablet: {
                breakpoint: { max: 959, min: 620 },
                items: 3,
            },
            mobile: {
                breakpoint: { max: 619, min: 401 },
                items: 2,
            },
            narrowMobile: {
                breakpoint: { max: 400, min: 0 },
                items: 1,
            },
        };
    }

    const handleCarouselChange = (node => {
        if (node !== null) {
            carouselInstance = node;
            setTimeout(() => {
                onChange();
            }, 3000);
        }
    });

    //window.addEventListener('resize', onChange);

    return (
        <React.Fragment>
            {isMounted && (
                <Carousel
                    ref={(el) => (handleCarouselChange(el))}
                    responsive={responsive}
                    swipeable={true}
                    draggable={false}
                    infinite={infinite}
                    autoPlay={autoPlaying}
                    autoPlaySpeed={props.autoPlaySpeed || 3000}
                    transitionDuration={props.transitionSpeed || 500}
                    keyBoardControl={true}
                    arrows={false}
                    showDots={!!props.showDots || false}
                    renderButtonGroupOutside={props.renderArrowsOutside !== null ? props.renderArrowsOutside : true}
                    customButtonGroup={<CustomControls {...props} onChange={onChange} autoPlaying={autoPlaying} setAutoPlaying={setAutoPlaying} infinite={infinite}/>}
                    afterChange={(previousSlide, { currentSlide }) => {
                        onChange();
                    }}

                >
                    {props.children}
                </Carousel>
            )}
        </React.Fragment>

    );
};

MultiCarousel.displayName = 'MultiCarousel';

export default MultiCarousel;
